import React, { useCallback, useEffect, useState, Component } from 'react';
import { Phone, Mail, Globe, MapPin, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/Button';
/*
 * Shared Dentico business-card artwork.
 * Every approved design property (dimensions, bleed, safe zones, logo lockup,
 * QR size/position, divider, contact + address layout, colours, fonts) lives
 * here so the EN / UA / RU documents stay byte-for-byte identical. The ONLY
 * per-language difference is the front-side subtitle passed in via props.
 *
 * UK standard business card
 * Trim:  85 × 55 mm
 * Bleed: 91 × 61 mm (3 mm each side)
 * Safe:  79 × 49 mm (3 mm inside trim)
 *
 * Dentico palette only:
 *   forest #2F4A3F · ivory #F8F5F0 · stone #EFE8DE · beige #D8C4A8 · charcoal #2B2D2F
 */
const FOREST = '#2F4A3F';
const IVORY = '#F8F5F0';
const BEIGE = '#D8C4A8';
const CHARCOAL = '#2B2D2F';
// QR links DIRECTLY to the clinic's WhatsApp chat (wa.me + E.164 number, no leading +).
const WHATSAPP_URL = 'https://wa.me/447345197820';
// High-res, brand-coloured QR: deep-green modules on white, generous quiet zone.
const QR_SRC = `https://api.qrserver.com/v1/create-qr-code/?size=1000x1000&margin=24&color=2F4A3F&bgcolor=FFFFFF&data=${encodeURIComponent(WHATSAPP_URL)}`;
const SCALE = 6; // 1mm ≈ 6px on screen
const BLEED_W = 91 * SCALE;
const BLEED_H = 61 * SCALE;
const TRIM_INSET = 3 * SCALE; // 3mm bleed → trim edge
const SAFE_INSET = 6 * SCALE; // 3mm bleed + 3mm safe margin
type PrintMode = 'none' | 'A-front' | 'A-back' | 'B-front' | 'B-back';
type Variant = 'A' | 'B';
export function BusinessCardArtwork({
  docName,
  subtitle
}: {
  /** Document title shown above the preview (e.g. "Dentico Business Card — Ukrainian"). */
  docName: string;
  /** Front-side subtitle, one entry per balanced line. */
  subtitle: string[];
}) {
  const [variant, setVariant] = useState<Variant>('A');
  const [printMode, setPrintMode] = useState<PrintMode>('none');
  const [showGuides, setShowGuides] = useState(true);
  const handlePrint = useCallback((mode: PrintMode) => {
    setPrintMode(mode);
  }, []);
  useEffect(() => {
    if (printMode !== 'none') {
      const timeout = setTimeout(() => {
        window.print();
        setPrintMode('none');
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [printMode]);
  const printingFront = printMode.endsWith('front');
  const printingBack = printMode.endsWith('back');
  return <div className="min-h-screen bg-stone-100 py-12 px-4 sm:px-6 lg:px-8 flex flex-col items-center print:bg-white print:p-0 print:m-0" data-id="element-809" data-mp="zz9lper94w95">
      <div className="max-w-7xl w-full space-y-10 print:space-y-0" data-id="element-810" data-mp="uz5fchk25hwn">
        {/* Header / controls — screen only */}
        <div className="text-center print:hidden" data-id="element-811" data-mp="19ryxybumme5j">
          <h1 className="text-3xl font-bold" style={{
          color: FOREST,
          fontFamily: '"Playfair Display", Georgia, serif'
        }} data-id="element-812" data-mp="14schzuneyjt1">
            {docName}
          </h1>
          <p className="mt-2 text-stone-500" data-id="element-813" data-mp="1en4hj40k3gqg">
            Double-sided · 85 × 55 mm · 3 mm bleed (91 × 61 mm) · print-ready
          </p>

          {/* Variant tabs */}
          <div className="mt-6 inline-flex rounded-full border border-stone-300 bg-white p-1" data-id="element-814" data-mp="22bayosnv63nn">
            {(['A', 'B'] as Variant[]).map(v => <button key={v} onClick={() => setVariant(v)} className="px-5 py-2 text-sm font-semibold rounded-full transition-colors" style={variant === v ? {
            backgroundColor: FOREST,
            color: '#fff'
          } : {
            color: FOREST
          }} data-id="element-815" data-mp="1tbnyga6vf3w6">
                {v === 'A' ? 'Option A — Clinic' : 'Option B — Team'}
              </button>)}
          </div>

          <div className="mt-5 flex items-center justify-center gap-3" data-id="element-816" data-mp="10nicxl7ztava">
            <button onClick={() => setShowGuides(!showGuides)} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full border border-stone-300 bg-white text-stone-600 hover:bg-stone-50 transition-colors" data-id="element-817" data-mp="230n8xbgsgmep">
              {showGuides ? <Eye className="w-3.5 h-3.5" data-id="element-818" data-mp="1p9i47uykd3xi" /> : <EyeOff className="w-3.5 h-3.5" data-id="element-819" data-mp="1p75s3yfhlnsd" />}
              {showGuides ? 'Guides On' : 'Guides Off'}
            </button>
            <div className="flex items-center gap-3 text-[10px] text-stone-400" data-id="element-820" data-mp="1fakjz4867wkb">
              <span className="flex items-center gap-1" data-id="element-821" data-mp="1b9h2kdah5sgz">
                <span className="w-3 h-0 border-t border-dashed border-red-400 inline-block" data-id="element-822" data-mp="1ba0m1yfrwox4" />{' '}
                Trim
              </span>
              <span className="flex items-center gap-1" data-id="element-823" data-mp="35ni63bgyz3ne">
                <span className="w-3 h-0 border-t border-dashed border-blue-400 inline-block" data-id="element-824" data-mp="35n019qg8rh3v" />{' '}
                Safe zone
              </span>
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-10 lg:gap-16 print:block" data-id="element-825" data-mp="385tuylgztw7s">
          {/* FRONT */}
          <div className="flex flex-col items-center gap-3" style={printingBack ? {
          display: 'none'
        } : undefined} data-id="element-826" data-mp="3hxzjfl81dvi8">
            <span className="text-xs font-medium text-stone-500 uppercase tracking-wider print:hidden" data-id="element-827" data-mp="2pawqr9q1kezw">
              Front
            </span>
            <Card data-id="element-828" data-mp="2z7huzsu8lm2o">
              <CardFront subtitle={subtitle} data-id="element-829" data-mp="2z701v52vfnyz" />
              {showGuides && <GuidesOverlay data-id="element-830" data-mp="2z9m7gz67btek" />}
            </Card>
          </div>

          {/* BACK */}
          <div className="flex flex-col items-center gap-3" style={printingFront ? {
          display: 'none'
        } : undefined} data-id="element-831" data-mp="336qsn8see43i">
            <span className="text-xs font-medium text-stone-500 uppercase tracking-wider print:hidden" data-id="element-832" data-mp="2fi78b79hgen5">
              Back
            </span>
            <Card data-id="element-833" data-mp="2am9pmuaqyw77">
              {variant === 'A' ? <CardBackClinic data-id="element-834" data-mp="2okgfbn5jlpvs" /> : <CardBackTeam data-id="element-835" data-mp="2oivn446paj13" />}
              {showGuides && <GuidesOverlay data-id="element-836" data-mp="2ohkk46l2xfb1" />}
            </Card>
          </div>
        </div>

        {/* Export buttons + specs — screen only */}
        <div className="flex flex-col items-center gap-5 pt-4 print:hidden" data-id="element-837" data-mp="1vurfhd4kjvvr">
          <div className="flex flex-wrap justify-center gap-4" data-id="element-838" data-mp="1rto16v2667wn">
            <Button onClick={() => handlePrint(`${variant}-front` as PrintMode)} size="lg" data-id="element-839" data-mp="1rvilc75m9vzo">
              Save Front as PDF
            </Button>
            <Button onClick={() => handlePrint(`${variant}-back` as PrintMode)} size="lg" data-id="element-840" data-mp="20tkkeh5siekb">
              Save Back as PDF
            </Button>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl px-6 py-4 max-w-md text-center shadow-sm" data-id="element-841" data-mp="184wzwnsv7t7t">
            <p className="text-xs font-semibold text-stone-700 mb-2" data-id="element-842" data-mp="3jrqsyd472iug">
              Print Settings
            </p>
            <div className="text-[11px] text-stone-500 space-y-1 leading-relaxed" data-id="element-843" data-mp="3oluyqt1q4kty">
              <p data-id="element-844" data-mp="3au6pqymruch7">
                In the print dialog select <strong data-id="element-845" data-mp="3fqx5tlykt82m">"Save as PDF"</strong>
              </p>
              <p data-id="element-846" data-mp="3fpccfznzzv6c">
                Paper size: <strong data-id="element-847" data-mp="7bwpfp8p7nuq">Custom — 91 × 61 mm</strong>
              </p>
              <p data-id="element-848" data-mp="79isfaugk1kj">
                Margins: <strong data-id="element-849" data-mp="bcyoa4ypdxw6">None</strong> · Scale: <strong data-id="element-850" data-mp="bc755tmalsms">100%</strong>
              </p>
              <p className="pt-1 text-stone-400" data-id="element-851" data-mp="3titvljcrv5vu">
                Export front & back separately, then upload both.
              </p>
            </div>
          </div>
        </div>
      </div>

      <PrintStyles data-id="element-852" data-mp="2edbyrjtrf3u" />
    </div>;
}
/* ── Card shell ─────────────────────────────────────────── */
function Card({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="card-bleed relative rounded-xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none" style={{
    width: BLEED_W,
    height: BLEED_H
  }} data-id="element-853" data-mp="2n4cegli3ovz2">
      {children}
    </div>;
}
/* ── Dentico wordmark (DENTICO + CLINIC aligned under TICO) ── */
function Wordmark({
  size = 'lg',
  color = IVORY,
  accent = BEIGE,
  withRule = true
}: {
  size?: 'lg' | 'sm';
  color?: string;
  accent?: string;
  withRule?: boolean;
}) {
  const big = size === 'lg';
  return <div className="inline-flex flex-col leading-none" data-id="element-854" data-mp="1dis1coctli1">
      <span style={{
      fontFamily: '"Playfair Display", Georgia, serif',
      color,
      fontSize: big ? 44 : 27,
      letterSpacing: big ? '0.22em' : '0.2em',
      fontWeight: 600
    }} data-id="element-855" data-mp="3sisbelhuf1io">
        DENTICO
      </span>
      {withRule && <span className="block self-stretch my-1.5" style={{
      height: 1,
      backgroundColor: accent,
      opacity: 0.85
    }} data-id="element-856" data-mp="k27alrzd7eqn" />}
      <span className="block text-right" style={{
      color,
      fontSize: big ? 11 : 9.5,
      letterSpacing: big ? '0.5em' : '0.42em',
      textTransform: 'uppercase',
      marginTop: withRule ? 0 : 4
    }} data-id="element-857" data-mp="nkmxmwa2u6ox">
        Clinic
      </span>
    </div>;
}
/* ── FRONT (shared by both variants) ───────────────────── */
function CardFront({
  subtitle
}: {
  subtitle: string[];
}) {
  const twoLine = subtitle.length > 1;
  return <div className="absolute inset-0" style={{
    backgroundColor: FOREST
  }} data-id="element-858" data-mp="1ak2m93f4jal7">
      <div className="absolute flex flex-col items-center justify-center text-center" style={{
      top: SAFE_INSET,
      left: SAFE_INSET,
      right: SAFE_INSET,
      bottom: SAFE_INSET
    }} data-id="element-859" data-mp="1faj0gpqrr08m">
        <Wordmark size="lg" color={IVORY} accent={BEIGE} withRule={false} data-id="element-860" data-mp="1parxg5sl4pp2" />
        <div className="flex flex-col items-center" style={{
        marginTop: 14,
        gap: 3
      }} data-id="element-861" data-mp="1u9d00bvb7lgo">
          {subtitle.map((line, i) => <span key={i} style={{
          fontSize: twoLine ? 10 : 12,
          letterSpacing: twoLine ? '0.14em' : '0.16em',
          textTransform: 'uppercase',
          color: BEIGE,
          fontFamily: '"DM Sans", sans-serif',
          fontWeight: 500,
          lineHeight: 1.35
        }} data-id="element-862" data-mp="e3d6ku1wllsj" data-mpi={i}>
              {line}
            </span>)}
        </div>
      </div>
    </div>;
}
/* ── Contact row (thin-line deep-green icon + text) ─────── */
function ContactRow({
  icon: Icon,
  children
}: {
  icon: typeof Phone;
  children: React.ReactNode;
}) {
  return <div className="flex items-center" style={{
    gap: 10
  }} data-id="element-863" data-mp="33l3y2u3ot0bs">
      <Icon className="shrink-0" style={{
      width: 16.5,
      height: 16.5,
      color: FOREST
    }} strokeWidth={1.6} data-id="element-864" data-mp="38k72ccbuf2ip" />
      <span style={{
      fontSize: 17,
      fontWeight: 500,
      color: FOREST,
      fontFamily: '"DM Sans", sans-serif',
      whiteSpace: 'nowrap'
    }} data-id="element-865" data-mp="311z6qvbqm4f2">
        {children}
      </span>
    </div>;
}
function QrBlock() {
  return <div className="flex flex-col items-center justify-center" data-id="element-866" data-mp="2mdcq8qadj6m9">
      <div style={{
      width: 104,
      height: 104,
      backgroundColor: '#FFFFFF',
      padding: 4
    }} data-id="element-867" data-mp="3k01xpfnuibfi">
        <img src={QR_SRC} alt="Scan to message Dentico Clinic on WhatsApp" className="w-full h-full object-contain" width={1000} height={1000} data-id="element-868" data-mp="3eyclvxssttqd" />
      </div>
      <span className="uppercase" style={{
      fontSize: 9,
      letterSpacing: '0.16em',
      color: FOREST,
      marginTop: 9,
      fontFamily: '"DM Sans", sans-serif',
      fontWeight: 600
    }} data-id="element-869" data-mp="3623jqbdfsh0j">
        WhatsApp Us
      </span>
    </div>;
}
/* ── BACK — Option A (Clinic) ──────────────────────────── */
function CardBackClinic() {
  return <div className="absolute inset-0" style={{
    backgroundColor: '#FFFFFF'
  }} data-id="element-870" data-mp="1m3lejy8yxaku">
      <div className="absolute flex flex-col" style={{
      top: SAFE_INSET,
      left: SAFE_INSET,
      right: SAFE_INSET,
      bottom: SAFE_INSET
    }} data-id="element-871" data-mp="1r2el16pu81bi">
        {/* Logo — nudged lower and left-aligned with the QR block's left edge */}
        <div className="mb-auto" style={{
        marginLeft: 40,
        paddingTop: 10
      }} data-id="element-872" data-mp="13d3s2beqbb7x">
          <Wordmark size="sm" color={FOREST} accent={BEIGE} withRule={false} data-id="element-873" data-mp="18cyg8774dmpa" />
        </div>

        {/* QR | divider | contacts — QR left edge aligned under the logo's D */}
        <div className="flex items-center justify-start" style={{
        gap: 24,
        marginLeft: 40
      }} data-id="element-874" data-mp="yein5yoj83d5">
          <QrBlock data-id="element-875" data-mp="frt2zugl8ir9" />
          <div style={{
          width: 1,
          height: 92,
          alignSelf: 'center',
          backgroundColor: BEIGE
        }} data-id="element-876" data-mp="aqlvlwicxll4" />
          <div className="flex flex-col" style={{
          gap: 15
        }} data-id="element-877" data-mp="oo0o5a3wq9hb">
            <ContactRow icon={Phone} data-id="element-878" data-mp="yo1910hfcxnm">+44 7345 197820</ContactRow>
            <ContactRow icon={Mail} data-id="element-879" data-mp="ymgdb7jdj8o6">info@denticoclinic.co.uk</ContactRow>
            <ContactRow icon={Globe} data-id="element-880" data-mp="toyfodat4fvh">denticoclinic.co.uk</ContactRow>
          </div>
        </div>

        {/* Address — centred footer line */}
        <div className="mt-auto pt-3" data-id="element-881" data-mp="13f9gvs33wayo">
          <div className="flex items-center justify-center" style={{
          gap: 8
        }} data-id="element-882" data-mp="13gca46nwiphr">
            <MapPin className="shrink-0" style={{
            width: 15.5,
            height: 15.5,
            color: FOREST
          }} strokeWidth={1.6} data-id="element-883" data-mp="1hcpr7eoiwovp" />
            <span style={{
            fontSize: 16,
            color: FOREST,
            fontFamily: '"DM Sans", sans-serif'
          }} data-id="element-884" data-mp="20j5mivxdd20c">
              360–364 City Road, London EC1V 2PY
            </span>
          </div>
        </div>
      </div>
    </div>;
}
/* ── BACK — Option B (Team) ────────────────────────────── */
function CardBackTeam() {
  return <div className="absolute inset-0" style={{
    backgroundColor: '#FFFFFF'
  }} data-id="element-885" data-mp="1hk1hdy8y7sdy">
      <div className="absolute flex flex-col" style={{
      top: SAFE_INSET,
      left: SAFE_INSET,
      right: SAFE_INSET,
      bottom: SAFE_INSET
    }} data-id="element-886" data-mp="1miuu3ogevfd2">
        {/* Top: logo + name/role */}
        <div className="flex items-start justify-between" data-id="element-887" data-mp="2y15swrnl9g1w">
          <div data-id="element-888" data-mp="32x330g89dq68">
            <span style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 600,
            color: FOREST,
            fontFamily: '"DM Sans", sans-serif'
          }} data-id="element-889" data-mp="32wl1p56qolrg">
              Full Name
            </span>
            <span style={{
            display: 'block',
            fontSize: 10,
            color: CHARCOAL,
            marginTop: 2,
            fontFamily: '"DM Sans", sans-serif'
          }} data-id="element-890" data-mp="hjety4aa0x6l">
              Role / Position
            </span>
            <span style={{
            display: 'block',
            fontSize: 8.5,
            color: '#8C7B63',
            marginTop: 4,
            letterSpacing: '0.04em',
            fontFamily: '"DM Sans", sans-serif'
          }} data-id="element-891" data-mp="3jn9bkp98ijm">
              GDC No. —
            </span>
          </div>
          <Wordmark size="sm" color={FOREST} accent={BEIGE} withRule={false} data-id="element-892" data-mp="1o0js8nhjy6bc" />
        </div>

        {/* Middle: QR | divider | contacts */}
        <div className="flex items-center justify-between my-auto" style={{
        gap: 14
      }} data-id="element-893" data-mp="3ifp8qvnl3qpq">
          <QrBlock data-id="element-894" data-mp="38j43qabkeflw" />
          <div style={{
          width: 1,
          alignSelf: 'stretch',
          backgroundColor: BEIGE,
          margin: '2px 0'
        }} data-id="element-895" data-mp="38jm51ld33k0o" />
          <div className="flex flex-col" style={{
          gap: 9
        }} data-id="element-896" data-mp="52n9of47096w">
            <ContactRow icon={Phone} data-id="element-897" data-mp="a0fbq1yjxbot">+44 7345 197820</ContactRow>
            <ContactRow icon={Mail} data-id="element-898" data-mp="3s7d5nc2wtzyl">info@denticoclinic.co.uk</ContactRow>
            <ContactRow icon={Globe} data-id="element-899" data-mp="3s9607t090tq8">denticoclinic.co.uk</ContactRow>
          </div>
        </div>

        {/* Address bottom */}
        <div className="pt-2" data-id="element-900" data-mp="2gs69h1tjumyb">
          <div className="flex items-center" style={{
          gap: 10
        }} data-id="element-901" data-mp="2gqv5p203tf77">
            <MapPin className="shrink-0" style={{
            width: 13,
            height: 13,
            color: FOREST
          }} strokeWidth={1.6} data-id="element-902" data-mp="2kuumllpaqg13" />
            <span style={{
            fontSize: 10.5,
            color: CHARCOAL,
            fontFamily: '"DM Sans", sans-serif'
          }} data-id="element-903" data-mp="2n86fi61fdec6">
              360–364 City Road, London EC1V 2PY
            </span>
          </div>
        </div>
      </div>
    </div>;
}
/* ── Trim + safe-zone guides (screen only) ─────────────── */
function GuidesOverlay() {
  return <div className="absolute inset-0 pointer-events-none print:hidden" data-id="element-904" data-mp="2492dvic7qmuj">
      <div className="absolute border border-dashed" style={{
      top: TRIM_INSET,
      left: TRIM_INSET,
      right: TRIM_INSET,
      bottom: TRIM_INSET,
      borderColor: 'rgba(239,68,68,0.45)'
    }} data-id="element-905" data-mp="24b5boojlxk9t" />
      <div className="absolute border border-dashed" style={{
      top: SAFE_INSET,
      left: SAFE_INSET,
      right: SAFE_INSET,
      bottom: SAFE_INSET,
      borderColor: 'rgba(59,130,246,0.35)'
    }} data-id="element-906" data-mp="16t57mzgv1ibf" />
    </div>;
}
function PrintStyles() {
  return <style data-id="element-907" data-mp="v784escx1795">{`
      @media print {
        @page { size: 91mm 61mm; margin: 0; }
        * { print-color-adjust: exact !important; -webkit-print-color-adjust: exact !important; }
        html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; overflow: hidden !important; }
        h1, button, nav, header, footer, .print\\:hidden, [class*="print:hidden"] { display: none !important; }
        .min-h-screen { display: block !important; min-height: 0 !important; padding: 0 !important; margin: 0 !important; background: #fff !important; }
        .max-w-7xl { max-width: none !important; padding: 0 !important; margin: 0 !important; }
        .card-bleed {
          position: fixed !important; top: 0 !important; left: 0 !important;
          width: ${BLEED_W}px !important; height: ${BLEED_H}px !important;
          border-radius: 0 !important; border: none !important; box-shadow: none !important;
          overflow: hidden !important; transform-origin: top left !important;
          transform: scale(calc(91mm / ${BLEED_W}px), calc(61mm / ${BLEED_H}px)) !important;
        }
        .card-bleed .pointer-events-none { display: none !important; }
      }
    `}</style>;
}